import React from "react";
import { Typography } from "@mui/material";
import {
  FinancialTableConfig,
  FinancialTableDataType,
  FINANCIAL_TABLE_STYLES,
} from "../constants/financialStatementTable";

interface FinancialTableProps {
  config: FinancialTableConfig;
}

// Format value based on data type
const formatValue = (value: string | number | null, dataType: FinancialTableDataType): string => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  // Handle division by zero or error values
  if (typeof value === "string" && value.includes("#DIV/0!")) {
    return "#DIV/0!";
  }

  switch (dataType) {
    case FinancialTableDataType.CURRENCY:
      if (typeof value === "number") {
        const formatted = Math.abs(value).toLocaleString("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });
        return value < 0 ? `-$${formatted}` : `$${formatted}`;
      }
      return String(value);

    case FinancialTableDataType.PERCENTAGE:
      if (typeof value === "number") {
        return `${value.toFixed(1)}%`;
      }
      return String(value);

    case FinancialTableDataType.NUMBER:
      if (typeof value === "number") {
        // For ratios like DSCR and ICR, show appropriate decimal places
        // Use toFixed with enough precision, but remove trailing zeros
        const formatted = value.toFixed(9);
        return formatted.replace(/\.?0+$/, "");
      }
      return String(value);

    case FinancialTableDataType.TEXT:
    default:
      return String(value);
  }
};

const FinancialTable: React.FC<FinancialTableProps> = ({ config }) => {
  const { title, columns, rows } = config;

  return (
    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        marginBottom: "20px",
      }}
    >
      {/* Main Header */}
      <thead>
        <tr>
          <th
            colSpan={columns.length + 2}
            style={{
              backgroundColor: FINANCIAL_TABLE_STYLES.HEADER_BACKGROUND,
              color: FINANCIAL_TABLE_STYLES.HEADER_TEXT_COLOR,
              padding: FINANCIAL_TABLE_STYLES.HEADER_PADDING,
              border: `${FINANCIAL_TABLE_STYLES.BORDER_WIDTH} solid ${FINANCIAL_TABLE_STYLES.BORDER_COLOR}`,
              textAlign: "left",
              fontWeight: "bold",
            }}
          >
            {title}
          </th>
        </tr>
        {/* Column Headers */}
        {columns?.length > 0 && (
          <tr>
            <th
              style={{
                backgroundColor: FINANCIAL_TABLE_STYLES.HEADER_BACKGROUND,
                color: FINANCIAL_TABLE_STYLES.HEADER_TEXT_COLOR,
                padding: FINANCIAL_TABLE_STYLES.HEADER_PADDING,
                border: `${FINANCIAL_TABLE_STYLES.BORDER_WIDTH} solid ${FINANCIAL_TABLE_STYLES.BORDER_COLOR}`,
                textAlign: "left",
                fontWeight: "bold",
              }}
            >
              {/* Row header column - empty or can have a label */}
            </th>
            {columns.map((column) => (
              <th
                key={column.key}
                style={{
                  backgroundColor: column.isHistorical
                    ? FINANCIAL_TABLE_STYLES.DATA_ROW_BACKGROUND
                    : FINANCIAL_TABLE_STYLES.HEADER_BACKGROUND,
                  color: column.isHistorical
                    ? FINANCIAL_TABLE_STYLES.DATA_ROW_TEXT_COLOR
                    : FINANCIAL_TABLE_STYLES.HEADER_TEXT_COLOR,
                  padding: FINANCIAL_TABLE_STYLES.HEADER_PADDING,
                  border: `${FINANCIAL_TABLE_STYLES.BORDER_WIDTH} solid ${FINANCIAL_TABLE_STYLES.BORDER_COLOR}`,
                  textAlign: "right",
                  fontWeight: "bold",
                  fontSize: FINANCIAL_TABLE_STYLES.HEADER_FONT_SIZE,
                }}
              >
                {column.label}
              </th>
            ))}
          </tr>
        )}
      </thead>

      {/* Table Body */}
      <tbody>
        {rows.map((row, rowIndex) => {
          const isSummaryRow = row.isSummary || row.type === "summary";
          const isKeyRow = row.isKeyRow;

          return (
            <tr key={row.key || rowIndex}>
              {/* Row Header */}
              <td
                style={{
                  // eslint-disable-next-line no-nested-ternary
                  backgroundColor: isSummaryRow
                    ? FINANCIAL_TABLE_STYLES.SUMMARY_ROW_BACKGROUND
                    : isKeyRow
                      ? FINANCIAL_TABLE_STYLES.ROW_HEADER_DARK_BACKGROUND
                      : FINANCIAL_TABLE_STYLES.DATA_ROW_BACKGROUND,
                  // eslint-disable-next-line no-nested-ternary
                  color: isSummaryRow
                    ? FINANCIAL_TABLE_STYLES.SUMMARY_ROW_TEXT_COLOR
                    : FINANCIAL_TABLE_STYLES.DATA_ROW_TEXT_COLOR,
                  padding: FINANCIAL_TABLE_STYLES.CELL_PADDING,
                  border: `${FINANCIAL_TABLE_STYLES.BORDER_WIDTH} solid ${FINANCIAL_TABLE_STYLES.BORDER_COLOR}`,
                  fontWeight: isKeyRow || isSummaryRow ? "bold" : "normal",
                }}
              >
                <Typography variant="body2">{row.label}</Typography>
              </td>

              {/* Data Cells */}
              {row.values.map((value, colIndex) => {
                const formattedValue = formatValue(value, row.dataType);

                // Determine background color for conditional coloring
                let cellBackgroundColor: string;
                
                if (
                  isSummaryRow &&
                  row.useConditionalColoring &&
                  value !== null &&
                  value !== undefined &&
                  value !== ""
                ) {
                  const numValue = typeof value === "string" ? parseFloat(value) : value;
                  if (!isNaN(numValue)) {
                    cellBackgroundColor =
                      numValue >= 0
                        ? FINANCIAL_TABLE_STYLES.POSITIVE_VALUE_BACKGROUND
                        : FINANCIAL_TABLE_STYLES.NEGATIVE_VALUE_BACKGROUND;
                  } else {
                    cellBackgroundColor = isSummaryRow
                      ? FINANCIAL_TABLE_STYLES.SUMMARY_ROW_BACKGROUND
                      : FINANCIAL_TABLE_STYLES.DATA_ROW_BACKGROUND;
                  }
                } else {
                  cellBackgroundColor = isSummaryRow
                    ? FINANCIAL_TABLE_STYLES.SUMMARY_ROW_BACKGROUND
                    : FINANCIAL_TABLE_STYLES.DATA_ROW_BACKGROUND;
                }

                const cellTextColor = isSummaryRow
                  ? FINANCIAL_TABLE_STYLES.SUMMARY_ROW_TEXT_COLOR
                  : FINANCIAL_TABLE_STYLES.DATA_ROW_TEXT_COLOR;

                return (
                  <td
                    key={`${row.key}-${colIndex}`}
                    style={{
                      backgroundColor: cellBackgroundColor,
                      color: cellTextColor,
                      padding: FINANCIAL_TABLE_STYLES.CELL_PADDING,
                      border: `${FINANCIAL_TABLE_STYLES.BORDER_WIDTH} solid ${FINANCIAL_TABLE_STYLES.BORDER_COLOR}`,
                      textAlign: "right",
                      fontWeight: isSummaryRow ? "bold" : "normal",
                    }}
                  >
                    <Typography variant="body2">{formattedValue}</Typography>
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default FinancialTable;
